import type { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import type { DataNode } from 'components/WorkspaceEditor/types'
import { shouldRenderAsProductType } from 'components/WorkspaceEditor/utils/contentItemViewNodesUtils'

interface ShouldHideElementOptions {
    /** The parent node containing this content item */
    node?: DataNode
    /** The content item to evaluate */
    contentItem: Partial<TextContentElement>
    /** Whether this is the first content item in the PT node */
    isPTFirstItem: boolean
}

/**
 * Determines if a PlainText element should be hidden in Drawing Designation mode.
 *
 * @param options Configuration options for visibility calculation
 * @returns true if the element should be hidden, false otherwise
 */
export const shouldHideElement = ({
    contentItem,
    isPTFirstItem,
    node,
}: ShouldHideElementOptions) => {
    // NEVER hide the standalone colon element - it should always render
    // regardless of Drawing Designation being enabled or not
    const isStandaloneColon =
        contentItem.type === 'Text' && contentItem.text?.trim() === ':'

    if (isStandaloneColon) {
        return false // Never hide the colon
    }

    // Only hide content for Product Type nodes, not for user-added/pasted paragraphs.
    const isProductTypeWithContent = Boolean(
        shouldRenderAsProductType(node) && node?.contentItemNodes?.[0]
    )

    // During optimistic updates, prependKeyedTextElement adds a _key element as the first item.
    // DesignationField's suffix only covers the _key element's value portion, not the remaining
    // split elements. Hiding those elements would lose the user's edit, so skip hiding entirely.
    const firstElementId = node?.contentItemNodes?.[0]?.id || ''
    const isOptimisticKeyPrepend = firstElementId.endsWith('_key')

    // Don't hide the first element (it renders the keyed text/designation)
    const isNotFirstElement = !isPTFirstItem

    // Only hide structural text elements (Text/FieldRef), not OptionGroups or other types
    const isHideableElementType =
        contentItem.type === 'Text' || contentItem.type === 'FieldRef'

    // Match id+text first to disambiguate duplicate IDs from track-changes splits;
    // fall back to id-only when text is undefined or no exact match found.
    // No id-only fallback: if text doesn't match, return -1 (treat as not found → not hidden).
    // The only caller that trims contentItem.text (trimContentItem) only affects index 0,
    // which is always protected by isPTFirstItem so currentIndex is irrelevant there.
    const currentIndex =
        node?.contentItemNodes?.findIndex(
            (n) => n.id === contentItem.id && n.text === contentItem.text
        ) ?? -1

    // Find the standalone colon element — marks the end of the key structure
    const colonElementIndex =
        node?.contentItemNodes?.findIndex(
            (n) => n.type === 'Text' && n.text?.trim() === ':'
        ) ?? -1

    // Find the first Text element whose text contains a colon (embedded colon, e.g. "Key:").
    // Used as a boundary when there is no standalone colon element.
    const embeddedColonElementIndex =
        node?.contentItemNodes?.findIndex(
            (n) =>
                n.type !== 'FieldRef' &&
                n.type !== 'OptionGroup' &&
                n.text?.includes(':')
        ) ?? -1

    const fieldRefIndex =
        node?.contentItemNodes?.findIndex((n) => n.type === 'FieldRef') ?? -1

    const firstOptionGroupIndex =
        node?.contentItemNodes?.findIndex((n) => n.type === 'OptionGroup') ?? -1

    // Only hide key-structural elements (FieldRef, standalone colon, whitespace between them).
    // Use colon element as boundary when present, otherwise fall back to OptionGroup boundary.
    // When neither boundary exists, hide all non-first elements (drawing designation mode default).
    // Elements after the colon or OptionGroup are value content and should render.
    // Elements not found in node.contentItemNodes (currentIndex === -1) are from optimistic updates
    // and should never be hidden since DesignationField doesn't render them.
    const isInKeyStructure = (() => {
        if (currentIndex === -1) return false
        if (colonElementIndex !== -1) return currentIndex <= colonElementIndex
        if (embeddedColonElementIndex !== -1) {
            const colonText =
                node?.contentItemNodes?.[embeddedColonElementIndex]?.text ?? ''
            const colonFollowsFieldRef =
                fieldRefIndex !== -1 &&
                embeddedColonElementIndex > fieldRefIndex
            const colonIsValueSeparator = colonText.trimStart().startsWith(':')
            if (colonFollowsFieldRef && colonIsValueSeparator) {
                // Strict less-than: the colon element itself is value content, not key structure
                return currentIndex < embeddedColonElementIndex
            }
            // Standard case: elements up to and including the colon element are key structure
            return currentIndex <= embeddedColonElementIndex
        }
        if (firstOptionGroupIndex !== -1)
            return currentIndex < firstOptionGroupIndex
        return true // No boundaries found - hide all non-first elements (Bug 2574397)
    })()

    const shouldHide =
        !isOptimisticKeyPrepend &&
        isProductTypeWithContent &&
        isNotFirstElement &&
        isHideableElementType &&
        isInKeyStructure

    return shouldHide
}
